import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatMoney, KIND_LABEL, CURRENCIES } from "@/lib/format";
import { Plus, ArrowLeft, FileText, FilePlus, Pencil, Trash2, FolderPlus } from "lucide-react";
import TransactionsTable from "@/components/TransactionsTable";
import TransactionDialog from "@/components/TransactionDialog";
import { toast } from "sonner";

const CFG = {
  customer: { endpoint: "/customers", route: "musteriler", title: "Müşteri", invoicesEndpoint: "/sales-invoices", invoiceRoute: "/satis-faturalari" },
  supplier: { endpoint: "/suppliers", route: "tedarikciler", title: "Tedarikçi", invoicesEndpoint: "/purchase-invoices", invoiceRoute: "/alis-faturalari" },
  employee: { endpoint: "/employees", route: "calisanlar", title: "Çalışan" },
  cash_register: { endpoint: "/cash-registers", route: "kasalar", title: "Kasa" },
};

export default function EntityDetail({ kind }) {
  const cfg = CFG[kind];
  const { id } = useParams();
  const nav = useNavigate();
  const [entity, setEntity] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [txOpen, setTxOpen] = useState(false);
  const [txPreset, setTxPreset] = useState({});
  const [confirmDel, setConfirmDel] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);

  // Inline project create/edit/delete for customer view
  const [projOpen, setProjOpen] = useState(false);
  const [projForm, setProjForm] = useState({ id: null, name: "", status: "active", start_date: "", end_date: "", notes: "" });
  const [projDel, setProjDel] = useState(null);

  // Inline invoice delete
  const [invoiceDel, setInvoiceDel] = useState(null);

  const load = async () => {
    const [e, tx] = await Promise.all([
      api.get(`${cfg.endpoint}/${id}`),
      api.get("/transactions", { params: { entity_kind: kind, entity_id: id } }),
    ]);
    setEntity(e.data);
    setTransactions(tx.data);
    if (cfg.invoicesEndpoint) {
      const inv = await api.get(cfg.invoicesEndpoint, { params: { entity_id: id } });
      setInvoices(inv.data);
    }
    if (kind === "customer") {
      const pr = await api.get("/projects", { params: { customer_id: id } });
      setProjects(pr.data);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, kind]);

  const openAction = (preset) => { setTxPreset(preset); setTxOpen(true); };

  const del = async () => {
    setConfirmDel(false);
    try {
      await api.delete(`${cfg.endpoint}/${id}`);
      toast.success("Silindi");
      nav(`/${cfg.route}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Silinemedi");
    }
  };

  const openEdit = () => {
    setEditForm({
      name: entity.name || "",
      phone: entity.phone || "",
      email: entity.email || "",
      address: entity.address || "",
      tax_no: entity.tax_no || "",
      notes: entity.notes || "",
      currency: entity.currency || "TRY",
      position: entity.position || "",
      salary: entity.salary || 0,
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm.name) { toast.error("Ad zorunlu"); return; }
    try {
      const body = { ...editForm, salary: Number(editForm.salary || 0) };
      await api.put(`${cfg.endpoint}/${id}`, body);
      toast.success("Güncellendi");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Güncellenemedi");
    }
  };

  // Project inline CRUD (customer view only)
  const openNewProject = () => {
    setProjForm({ id: null, name: "", status: "active", start_date: "", end_date: "", notes: "" });
    setProjOpen(true);
  };
  const openEditProject = (p) => {
    setProjForm({
      id: p.id, name: p.name || "", status: p.status || "active",
      start_date: p.start_date?.slice(0,10) || "",
      end_date: p.end_date?.slice(0,10) || "",
      notes: p.notes || "",
    });
    setProjOpen(true);
  };
  const saveProject = async () => {
    if (!projForm.name) { toast.error("Proje adı zorunlu"); return; }
    const body = {
      customer_id: id,
      name: projForm.name,
      status: projForm.status,
      start_date: projForm.start_date ? new Date(projForm.start_date).toISOString() : null,
      end_date: projForm.end_date ? new Date(projForm.end_date).toISOString() : null,
      notes: projForm.notes || "",
    };
    try {
      if (projForm.id) {
        await api.put(`/projects/${projForm.id}`, body);
        toast.success("Proje güncellendi");
      } else {
        await api.post("/projects", body);
        toast.success("Proje oluşturuldu");
      }
      setProjOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi");
    }
  };
  const confirmDeleteProject = async () => {
    try {
      await api.delete(`/projects/${projDel.id}`);
      toast.success("Proje silindi");
      setProjDel(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Silinemedi");
    }
  };

  // Invoice delete
  const confirmDeleteInvoice = async () => {
    try {
      await api.delete(`${cfg.invoicesEndpoint}/${invoiceDel.id}`, { params: { delete_payments: true } });
      toast.success("Fatura silindi");
      setInvoiceDel(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Silinemedi");
    }
  };

  // Nav with returnTo state so back button returns to this customer page
  const goBackable = (path) => nav(path, { state: { returnTo: `/${cfg.route}/${id}` } });

  if (!entity) return <div className="text-zinc-500">Yükleniyor...</div>;
  const bal = entity.balance || 0;
  return (
    <div className="space-y-6" data-testid={`${kind}-detail-page`}>
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => nav(`/${cfg.route}`)}><ArrowLeft size={16} /></Button>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-zinc-500">{cfg.title}</div>
          <h1 className="text-3xl mt-1">{entity.name}</h1>
        </div>
        <Button variant="outline" onClick={openEdit} data-testid="entity-edit-btn"><Pencil size={14} className="mr-1"/>Düzenle</Button>
        <Button variant="outline" onClick={()=>setConfirmDel(true)} data-testid="entity-delete-btn">Sil</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-5 border border-zinc-200 shadow-none md:col-span-1">
          <div className="text-xs uppercase tracking-widest text-zinc-500">Bakiye</div>
          <div className={`mt-3 text-2xl number ${bal >= 0 ? "pos" : "neg"}`}>{formatMoney(bal, entity.currency)}</div>
          <div className="text-xs text-zinc-500 mt-1">
            {kind === "customer" && (bal > 0 ? "Bize borçlu" : bal < 0 ? "Biz borçluyuz" : "Denk")}
            {kind === "supplier" && (bal > 0 ? "Biz borçluyuz" : bal < 0 ? "Onlar borçlu" : "Denk")}
            {kind === "employee" && (bal > 0 ? "Biz borçluyuz" : bal < 0 ? "Çalışan borçlu" : "Denk")}
            {kind === "cash_register" && "Kasa bakiyesi"}
          </div>
        </Card>
        <Card className="p-5 border border-zinc-200 shadow-none md:col-span-3 text-sm text-zinc-600 space-y-1">
          {entity.phone && <div><span className="text-zinc-500">Telefon: </span>{entity.phone}</div>}
          {entity.email && <div><span className="text-zinc-500">E-posta: </span>{entity.email}</div>}
          {entity.address && <div><span className="text-zinc-500">Adres: </span>{entity.address}</div>}
          {entity.tax_no && <div><span className="text-zinc-500">Vergi No: </span>{entity.tax_no}</div>}
          {entity.position && <div><span className="text-zinc-500">Pozisyon: </span>{entity.position}</div>}
          {entity.salary != null && kind === "employee" && <div><span className="text-zinc-500">Maaş: </span><span className="number">{formatMoney(entity.salary, entity.currency)}</span></div>}
          {entity.notes && <div className="text-zinc-500 italic">"{entity.notes}"</div>}
        </Card>
      </div>

      <div className="flex flex-wrap gap-2">
        {kind === "customer" && <>
          <Button data-testid="new-sales-invoice-from-customer" onClick={()=>goBackable(`/satis-faturalari/yeni?entity_id=${id}`)} className="bg-zinc-900 hover:bg-zinc-800"><FilePlus size={16} className="mr-1"/>Satış Faturası Kes</Button>
          <Button data-testid="add-receipt-btn" variant="outline" onClick={()=>openAction({category:"receipt", from_kind:"customer", from_id:id, to_kind:"cash_register", currency: entity.currency})}><Plus size={16} className="mr-1"/>Tahsilat Al</Button>
          <Button variant="outline" onClick={()=>openAction({category:"payment", from_kind:"cash_register", to_kind:"customer", to_id:id, currency: entity.currency})}>Müşteriye Ödeme</Button>
          <Button data-testid="new-project-for-customer" variant="outline" onClick={openNewProject}><FolderPlus size={16} className="mr-1"/>Proje Oluştur</Button>
        </>}
        {kind === "supplier" && <>
          <Button data-testid="new-purchase-invoice-from-supplier" onClick={()=>goBackable(`/alis-faturalari/yeni?entity_id=${id}`)} className="bg-zinc-900 hover:bg-zinc-800"><FilePlus size={16} className="mr-1"/>Alış Faturası Gir</Button>
          <Button data-testid="add-payment-btn" variant="outline" onClick={()=>openAction({category:"payment", from_kind:"cash_register", to_kind:"supplier", to_id:id, currency: entity.currency})}><Plus size={16} className="mr-1"/>Ödeme Yap</Button>
          <Button variant="outline" onClick={()=>openAction({category:"receipt", from_kind:"supplier", from_id:id, to_kind:"cash_register", currency: entity.currency})}>Tedarikçiden Tahsilat</Button>
        </>}
        {kind === "employee" && <>
          <Button onClick={()=>openAction({category:"salary", from_kind:"cash_register", to_kind:"employee", to_id:id, currency: entity.currency})}><Plus size={16} className="mr-1"/>Maaş Öde</Button>
          <Button variant="outline" onClick={()=>openAction({category:"bonus", from_kind:"cash_register", to_kind:"employee", to_id:id, currency: entity.currency})}>Bonus</Button>
          <Button variant="outline" onClick={()=>openAction({category:"loan", from_kind:"cash_register", to_kind:"employee", to_id:id, currency: entity.currency})}>Çalışana Borç Ver</Button>
          <Button variant="outline" onClick={()=>openAction({category:"borrow_from_employee", from_kind:"employee", from_id:id, to_kind:"external", currency: entity.currency})} data-testid="borrow-from-emp-btn">Çalışana Borçlan</Button>
          <Button variant="outline" onClick={()=>openAction({category:"repay_to_employee", from_kind:"cash_register", to_kind:"employee", to_id:id, currency: entity.currency})} data-testid="repay-emp-btn">Borcumu Geri Öde</Button>
        </>}
        {kind === "cash_register" && <>
          <Button onClick={()=>openAction({category:"manual_deposit", from_kind:"external", to_kind:"cash_register", to_id:id, currency: entity.currency})}><Plus size={16} className="mr-1"/>Para Ekle</Button>
          <Button variant="outline" onClick={()=>openAction({category:"manual_withdraw", from_kind:"cash_register", from_id:id, to_kind:"external", currency: entity.currency})}>Para Çek</Button>
        </>}
      </div>

      <Tabs defaultValue="transactions">
        <TabsList>
          <TabsTrigger value="transactions" data-testid="tab-transactions">Cari Hareketler</TabsTrigger>
          {cfg.invoicesEndpoint && <TabsTrigger value="invoices" data-testid="tab-invoices">Faturalar</TabsTrigger>}
          {kind === "customer" && <TabsTrigger value="projects" data-testid="tab-customer-projects">Projeler</TabsTrigger>}
        </TabsList>
        <TabsContent value="transactions" className="mt-4">
          <TransactionsTable items={transactions} showBalance onChanged={load} onRowClick={(t)=>{
            if (t.invoice_id && cfg.invoiceRoute) goBackable(`${cfg.invoiceRoute}/${t.invoice_id}`);
          }} />
        </TabsContent>
        {cfg.invoicesEndpoint && (
          <TabsContent value="invoices" className="mt-4">
            <div className="bg-white rounded-md border border-zinc-200 overflow-hidden">
              <table className="acc">
                <thead><tr><th>No</th><th>Tarih</th><th className="num">Toplam</th><th style={{width:100}}></th></tr></thead>
                <tbody>
                  {invoices.length === 0 && <tr><td colSpan={4} className="text-center text-zinc-500 py-8">Fatura yok</td></tr>}
                  {invoices.map(inv => (
                    <tr key={inv.id} onClick={()=>goBackable(`${cfg.invoiceRoute}/${inv.id}`)} data-testid={`invoice-row-${inv.id}`} className="cursor-pointer">
                      <td className="font-medium">{inv.invoice_number}</td>
                      <td className="text-zinc-600">{inv.date?.slice(0,10)}</td>
                      <td className="num">{formatMoney(inv.total, inv.currency)}</td>
                      <td className="flex gap-1" onClick={(e)=>e.stopPropagation()}>
                        <button
                          data-testid={`edit-invoice-${inv.id}`}
                          onClick={()=>goBackable(`${cfg.invoiceRoute}/duzenle/${inv.id}`)}
                          className="text-zinc-400 hover:text-zinc-900"
                          title="Düzenle"
                        ><Pencil size={14}/></button>
                        <button
                          data-testid={`delete-invoice-${inv.id}`}
                          onClick={()=>setInvoiceDel(inv)}
                          className="text-zinc-400 hover:text-red-600"
                          title="Sil"
                        ><Trash2 size={14}/></button>
                        <FileText size={14} className="text-zinc-400 ml-1" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}
        {kind === "customer" && (
          <TabsContent value="projects" className="mt-4 space-y-3">
            <div className="bg-white rounded-md border border-zinc-200 overflow-hidden">
              <table className="acc">
                <thead><tr><th>Ad</th><th>Başlangıç</th><th>Bitiş</th><th>Durum</th><th style={{width:100}}></th></tr></thead>
                <tbody>
                  {projects.length === 0 && <tr><td colSpan={5} className="text-center text-zinc-500 py-8">Proje yok</td></tr>}
                  {projects.map(p => (
                    <tr key={p.id} data-testid={`customer-project-row-${p.id}`} onClick={()=>goBackable(`/projeler/${p.id}`)} className="cursor-pointer">
                      <td className="font-medium">{p.name}</td>
                      <td className="text-zinc-600">{p.start_date?.slice(0,10) || "-"}</td>
                      <td className="text-zinc-600">{p.end_date?.slice(0,10) || "-"}</td>
                      <td className="text-zinc-600">{p.status}</td>
                      <td className="flex gap-1" onClick={(e)=>e.stopPropagation()}>
                        <button
                          data-testid={`edit-project-${p.id}`}
                          onClick={()=>openEditProject(p)}
                          className="text-zinc-400 hover:text-zinc-900"
                          title="Düzenle"
                        ><Pencil size={14}/></button>
                        <button
                          data-testid={`delete-project-${p.id}`}
                          onClick={()=>setProjDel(p)}
                          className="text-zinc-400 hover:text-red-600"
                          title="Sil"
                        ><Trash2 size={14}/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}
      </Tabs>

      <TransactionDialog open={txOpen} onClose={()=>setTxOpen(false)} onSaved={load} preset={txPreset} title="Yeni İşlem" />

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent data-testid="entity-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{cfg.title} Sil</AlertDialogTitle>
            <AlertDialogDescription>
              "{entity.name}" silinsin mi? Bu işlem geri alınamaz. Geçmiş hareketler ve faturalar korunur.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="entity-delete-cancel">İptal</AlertDialogCancel>
            <AlertDialogAction data-testid="entity-delete-confirm-btn" onClick={del} className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={projOpen} onOpenChange={setProjOpen}>
        <DialogContent data-testid="customer-project-dialog">
          <DialogHeader><DialogTitle>{projForm.id ? "Projeyi Düzenle" : "Yeni Proje"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Proje Adı</Label><Input data-testid="customer-project-name" value={projForm.name} onChange={(e)=>setProjForm({...projForm, name: e.target.value})} /></div>
            <div><Label>Başlangıç</Label><Input type="date" value={projForm.start_date} onChange={(e)=>setProjForm({...projForm, start_date: e.target.value})} /></div>
            <div><Label>Bitiş</Label><Input type="date" value={projForm.end_date} onChange={(e)=>setProjForm({...projForm, end_date: e.target.value})} /></div>
            <div className="col-span-2">
              <Label>Durum</Label>
              <Select value={projForm.status} onValueChange={(v)=>setProjForm({...projForm, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktif</SelectItem>
                  <SelectItem value="completed">Tamamlandı</SelectItem>
                  <SelectItem value="cancelled">İptal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Notlar</Label><Textarea rows={2} value={projForm.notes} onChange={(e)=>setProjForm({...projForm, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setProjOpen(false)}>İptal</Button>
            <Button data-testid="customer-project-save" onClick={saveProject} className="bg-zinc-900 hover:bg-zinc-800">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!projDel} onOpenChange={(v)=>{ if(!v) setProjDel(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Projeyi Sil</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{projDel?.name}&quot; silinsin mi? Faturaların bağı kaldırılır. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-customer-project" onClick={confirmDeleteProject} className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!invoiceDel} onOpenChange={(v)=>{ if(!v) setInvoiceDel(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Faturayı Sil</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{invoiceDel?.invoice_number}&quot; numaralı fatura silinsin mi? İlişkili tahsilat/ödeme kayıtları da silinecektir. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-customer-invoice" onClick={confirmDeleteInvoice} className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="entity-edit-dialog">
          <DialogHeader><DialogTitle>{cfg.title} Düzenle</DialogTitle></DialogHeader>
          {editForm && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Ad</Label><Input data-testid="entity-edit-name" value={editForm.name} onChange={(e)=>setEditForm({...editForm, name: e.target.value})} /></div>
              {kind !== "cash_register" && <>
                <div><Label>Telefon</Label><Input value={editForm.phone} onChange={(e)=>setEditForm({...editForm, phone: e.target.value})} /></div>
                <div><Label>E-posta</Label><Input value={editForm.email} onChange={(e)=>setEditForm({...editForm, email: e.target.value})} /></div>
                <div className="col-span-2"><Label>Adres</Label><Input value={editForm.address} onChange={(e)=>setEditForm({...editForm, address: e.target.value})} /></div>
                <div><Label>Vergi No</Label><Input value={editForm.tax_no} onChange={(e)=>setEditForm({...editForm, tax_no: e.target.value})} /></div>
              </>}
              <div>
                <Label>Para Birimi</Label>
                <Select value={editForm.currency} onValueChange={(v)=>setEditForm({...editForm, currency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {kind === "employee" && <>
                <div><Label>Pozisyon</Label><Input value={editForm.position} onChange={(e)=>setEditForm({...editForm, position: e.target.value})} /></div>
                <div><Label>Maaş</Label><Input type="number" step="0.01" value={editForm.salary} onChange={(e)=>setEditForm({...editForm, salary: e.target.value})} /></div>
              </>}
              {kind !== "cash_register" && <div className="col-span-2"><Label>Notlar</Label><Textarea value={editForm.notes} onChange={(e)=>setEditForm({...editForm, notes: e.target.value})} rows={2} /></div>}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setEditOpen(false)}>İptal</Button>
            <Button data-testid="entity-edit-save" onClick={saveEdit} className="bg-zinc-900 hover:bg-zinc-800">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
